<template>
  <teleport to="body" id="agEditPanel">
    <div v-if="modelValue">
      <div class="edit-panel-overlay" @click="onCancel"></div>
      <div class="edit-panel-container">
        <div class="border-bottom">
          <div class="d-flex justify-content-between align-items-center" style="margin-top: 10px;">
            <div class="d-flex align-items-center gap-2">
              <i v-if="canGoBack" class="pi pi-chevron-left" style="cursor: pointer;" @click="$emit('back')"></i>
              <h5 class="m-0 panel-title">{{ title }}</h5>
            </div>
            <Button class="btn-icon" icon="pi pi-times" variant="text" rounded aria-label="Cancel" @click="onCancel"
              size="normal" />
          </div>
          <ToggleSwitch v-if="showEnabled" v-model="formData.enabled" :label="$t('enabled').toUpperCase()" :show-info="true"
            :tooltip="formType === 'appGroup' ? tooltips.appGroup.enableDisable : tooltips.shared.enableDisable"
            :titleEnabled="true" :disabled="isFileAccessReadOnlyLocked" />
        </div>

        <div class="overflow-auto" style="max-height: calc(100% - 120px);">
          <component :is="formComponent" v-model:form="formData" :row="row" :formType="formType" :enumKey="enumKey"
            @edit="onChildEdit" @validation="onValidation" :linked-app="linkedApp" @linkedProcess="onLinkedProcess"/>
        </div>

        <div v-if="canGoBack && formType === 'fileTypePolicy'" class="panel-warning">
          <i class="pi pi-exclamation-triangle mr-2"></i>
          These settings override any other settings in AppGroup rules.
        </div>

        <div class="border-top p-3 d-flex justify-content-end gap-2 buttons-div">
          <button class="btn" style="border: 1px solid #D2D9E2;" @click="onCancel">Cancel</button>
          <button class="btn success-btn" @click="onSave" :disabled="saving || isFileAccessReadOnlyLocked">
            <span>Save</span>
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, onBeforeUnmount, computed } from 'vue'
import Button from "primevue/button";
import ToggleSwitch from './forms/components/toggle-switch.vue'
import tooltips from '@/assets/tooltips/en.json'
import { useStore } from "@/store";
import { AppGroupTypes } from '@/helpers/enums/AppGroupTypes';
import Swal from 'sweetalert2'
import { GeneralActionTypes } from '@/store/modules/general/action-types'
import { useRouter } from 'vue-router'
const store = useStore();
const router = useRouter();

const props = defineProps({
  modelValue: Boolean,
  title: {
    type: String,
    default: 'Edit Panel'
  },
  formComponent: {
    type: [Object, Function],
    required: true
  },
  form: {
    type: Object,
    default: () => ({
    })
  },
  enumKey: { type: String, default: 'AccessControl' },
  formType: { type: String, default: '' },
  linkedApp: { type: Boolean, default: false },
  canGoBack: { type: Boolean, default: false },
  showEnabled: {
    type: Boolean,
    default: true
  },
  saving: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue', 'save', 'cancel', 'edit', 'back'])
const formData = ref({ ...props.form })
const appGroupInfo = computed(() => store.state.general.appGroupInfo || {});
const isStdOrPrivate = computed(() => {
  const t = Number(appGroupInfo.value?.appGroupType)
  return t === AppGroupTypes.StandardContainment || t === AppGroupTypes.Private
});
const isAddMode = computed(() => Boolean(formData.value?.__isAdd === true))
const isEditMode = computed(() => Boolean(formData.value?.__isEdit === true))
const isReadOnlyAccess = computed(() => !isAddMode.value && isEditMode.value )

const isFileAccessReadOnlyLocked = computed(() => props.formType === 'fileAccess' && isStdOrPrivate.value && isReadOnlyAccess.value);
const hasValidationError = ref(false)
const validationMessage = computed(() => {
  if (props.formType === 'process' || props.formType === 'fileAccess' || props.formType === 'codeLoad') {
    return 'The Folder/File name path cannot exceed 260 characters.'
  }
  if (props.formType === 'registry') {
    return 'Registry Key cannot exceed 260 characters.'
  }
  return 'Path length cannot exceed 260 characters.'
})
watch(() => props.form, (newVal) => {
  const incoming = { ...(newVal || {}) };
  const defaults = {
    enabled: true,
    aecSecurityAllowRun: true,//process
    moduleSecurityAllowRun: false,
    accessControl: (Number(appGroupInfo.value?.appGroupType) === AppGroupTypes.PowerApps || props.formType === 'process') ? 2 : 1,
    registryHive: 2,//registry access
    appliesTo: 1,
    allowToRun: true,//code load
    memorySecurity: 2,
    inheritance: 1,
    moduleControl: false,
    applicationExecutionControl: true,
    childProcess: true
  };
  const merged = { ...defaults, ...incoming };
  const isNew = (incoming.id ?? 0) === 0;
  merged.memorySecurity = isNew
    ? ((incoming.memorySecurity ?? 0) === 0 ? defaults.memorySecurity : incoming.memorySecurity)
    : (incoming.memorySecurity ?? defaults.memorySecurity);
  merged.inheritance = isNew
    ? ((incoming.inheritance ?? 0) === 0 ? defaults.inheritance : incoming.inheritance)
    : (incoming.inheritance ?? defaults.inheritance);
  formData.value = merged;
}, { deep: true, immediate: true })

function lockBodyScroll() {
  try {
    const body = document.body
    const html = document.documentElement
    body.dataset.prevOverflow = body.style.overflow || ''
    html.dataset.prevOverflow = html.style.overflow || ''
    body.style.overflow = 'hidden'
    html.style.overflow = 'hidden'
  } catch (e) { /* no-op */ }
}

function unlockBodyScroll() {
  try {
    const body = document.body
    const html = document.documentElement
    if (body.dataset.prevOverflow !== undefined) {
      body.style.overflow = body.dataset.prevOverflow
      delete body.dataset.prevOverflow
    } else {
      body.style.overflow = ''
    }
    if (html.dataset.prevOverflow !== undefined) {
      html.style.overflow = html.dataset.prevOverflow
      delete html.dataset.prevOverflow
    } else {
      html.style.overflow = ''
    }
  } catch (e) { /* no-op */ }
}

watch(() => props.modelValue, (open) => {
  if (open) lockBodyScroll(); else unlockBodyScroll();
}, { immediate: true })

function onSave() {
  if (hasValidationError.value) {
    Swal.fire('Validation error', validationMessage.value, 'error')
    return
  }
  const payload = { ...formData.value };
  if (props.formType === 'fileTypePolicy' && Array.isArray(payload.fileExtensionApps)) {
    payload.fileExtensionApps = payload.fileExtensionApps.map((item) => {
      const isAny = item?.isFolderMapIdAny === true && (item.folderMap === 0 || item.folderMap === null);
      return isAny ? { ...item, folderMap: -1 } : item;
    });
  }
  emit('save', payload)
}

function onCancel() {
  if (props.canGoBack && props.formType === 'fileTypePolicy') {
    emit('back')
    return
  }
  emit('cancel')
  emit('update:modelValue', false)
  unlockBodyScroll()
}

function onChildEdit(payload) {
  emit('edit', payload)
}
function onValidation(valid) {
  hasValidationError.value = valid === false
}
async function onLinkedProcess(payload) {
  try {
    const processId = payload?.form?.processId ?? null;
    const appgroupId = payload?.form?.appGroupId ?? null;

    if (!processId || !appgroupId) {
      await Swal.fire('Linked Process', 'Missing required data to open the linked process rule.', 'warning');
      return;
    }

    const routeData = router.resolve({
      name: 'appGroupDetail',
      query: {
        id: String(appgroupId),
        tab: 'process',
        ruleId: processId
      },
    });

    window.open(routeData.href, '_blank');
  } catch (e) {
    console.error('Failed to open linked process rule for the given file access rule.', e);
    await Swal.fire('Error', 'Failed to fetch the linked process rule.', 'error');
  }
}

onBeforeUnmount(() => {
  unlockBodyScroll()
})
</script>

<style scoped>
.edit-panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1040;
}

.edit-panel-container {
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: 550px;
  background-color: #fff;
  z-index: 1050;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.2);
  animation: slideInRight 0.3s ease-out;
  overflow-y: auto;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
  }

  to {
    transform: translateX(0);
  }
}

.btn-icon {
  background: transparent;
  border: none;
  color: #000;
  box-shadow: none;
  padding: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

::v-deep(.btn-icon.p-button.p-button-text:hover) {
  background-color: #e6f0fb;
}

.success-btn {
  background-color: #003060;
  color: #fff;
}

.buttons-div button {
  width: 30%;
}

.panel-title {
  color: #164B6C;
  font-weight: bold;
}

.border-bottom {
  padding-bottom: 10px;
  margin-bottom: 10px;
  padding: 1rem;
}

.panel-warning {
  background: #fac812;
  color: #000;
  border-top: 1px solid #FFE082;
  border-bottom: 1px solid #FFE082;
  padding: 10px 14px;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 1rem;
  margin-right: 2rem;
  justify-content: center;
  text-align: center;

}

.buttons-div .btn {
  transition: background 120ms ease, color 120ms ease, transform 80ms ease, box-shadow 120ms ease, filter 120ms ease;
}

.buttons-div .btn:not(.success-btn):hover {
  background-color: #f8f8f8;
  color: #5e5e5e;
}

.buttons-div .btn:not(.success-btn):active {
  transform: translateY(1px);
  box-shadow: none;
}

.buttons-div .success-btn:hover {
  filter: brightness(1.08);
  box-shadow: 0 6px 14px rgba(22, 75, 108, 0.35);
}

.buttons-div .success-btn:active {
  filter: brightness(0.92);
  transform: translateY(1px);
  box-shadow: 0 2px 6px rgba(22, 75, 108, 0.2);
}

::v-deep(.p-checkbox .p-checkbox-box) {
  border: 1px solid #3377B8 !important;
}

::v-deep(.p-checkbox:not(.p-checkbox-disabled) .p-checkbox-box:hover) {
  border-color: #3377B8 !important;
}

::v-deep(.p-checkbox.p-focus .p-checkbox-box) {
  box-shadow: 0 0 0 0.2rem rgba(51, 119, 184, 0.15);
  border-color: #3377B8 !important;
}

::v-deep(.p-checkbox .p-checkbox-box.p-highlight) {
  border-color: #3377B8 !important;
}
</style>
